import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useLocation } from 'react-router';

/**
 * Como `useState`, pero lo elegido en una pantalla (la búsqueda, el filtro,
 * el mes que se mira) sigue ahí al volver a ella con «atrás». Se guarda por
 * entrada del historial (la `key` de la ubicación, que es la misma al
 * volver); al entrar de nuevo desde otra pantalla, empieza de cero.
 */

const estados = new Map<string, unknown>();
/** Entradas guardadas como mucho (las más viejas se olvidan). */
const MAXIMO = 200;

function guardar(clave: string, valor: unknown) {
  estados.delete(clave);
  estados.set(clave, valor);
  if (estados.size > MAXIMO) {
    const primera = estados.keys().next().value;
    if (primera != null) {
      estados.delete(primera);
    }
  }
}

/** Para las pruebas. */
export function olvidarEstadosDeVista() {
  estados.clear();
}

export function useEstadoDeVista<T>(
  nombre: string,
  inicial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
  const { key } = useLocation();
  const clave = `${key}:${nombre}`;
  const [valor, setValor] = useState<T>(() =>
    estados.has(clave)
      ? (estados.get(clave) as T)
      : typeof inicial === 'function'
        ? (inicial as () => T)()
        : inicial,
  );
  const cambiar = useCallback<Dispatch<SetStateAction<T>>>(
    accion =>
      setValor(previo => {
        const nuevo =
          typeof accion === 'function'
            ? (accion as (p: T) => T)(previo)
            : accion;
        guardar(clave, nuevo);
        return nuevo;
      }),
    [clave],
  );
  return [valor, cambiar];
}
